import React, { useEffect, useMemo, useState } from 'react';
import { useEditableContent, invalidateEditableContent } from '../hooks/useEditableContent';
import { useAuth } from '../hooks/useSupabase';
import supabase from '../lib/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEdit, FiSave, FiX } = FiIcons;

/**
 * EditableContent
 * - Reads page content from the batched useEditableContent(page) hook.
 * - Allows authenticated admins to edit the selected content block.
 * - Prefers the canonical content field over legacy content_html so stale
 *   HTML cannot override newer content updates.
 */
const EditableContent = ({
  page,
  section,
  contentKey,
  defaultValue = '',
  className = '',
  tag = 'div',
  multiline = false,
  children,
  ...props
}) => {
  const { user } = useAuth();
  const { rows, loading, error } = useEditableContent(page);

  const row = useMemo(() => (
    (rows || []).find((item) => item.section === section && item.content_key === contentKey) || null
  ), [rows, section, contentKey]);

  const storedContent = row?.content || row?.content_html || '';
  const rawContent = storedContent || defaultValue || children?.toString() || '';

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editValue, setEditValue] = useState(rawContent);
  const [localContent, setLocalContent] = useState(rawContent);

  useEffect(() => {
    if (!isEditing) {
      setLocalContent(rawContent);
      setEditValue(rawContent);
    }
  }, [rawContent, isEditing]);


  const handleEdit = () => {
    setEditValue(localContent);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditValue(localContent);
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      const value = editValue.trim();

      if (row?.id) {
        const { error: updateError } = await supabase
          .from('editable_content_aox1234')
          .update({
            content: value,
            content_html: null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', row.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('editable_content_aox1234')
          .insert([{
            page,
            section,
            key: contentKey,
            content: value,
            content_html: null,
          }]);

        if (insertError) throw insertError;
      }

      setLocalContent(value);
      setIsEditing(false);
      invalidateEditableContent(page);
    } catch (saveError) {
      console.error('EditableContent save failed:', saveError);
      alert(`Αποτυχία αποθήκευσης: ${saveError.message || 'Άγνωστο σφάλμα'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const renderContent = (value) => {
    const resolved = value || defaultValue || children?.toString() || '';

    if (typeof resolved !== 'string') return resolved;

    if (/<[^>]*>/g.test(resolved)) {
      return <span dangerouslySetInnerHTML={{ __html: resolved }} />;
    }

    if (typeof document === 'undefined') return resolved;

    const textArea = document.createElement('textarea');
    textArea.innerHTML = resolved;
    return textArea.value;
  };

  const TagName = tag;

  if (loading) {
    return (
      <TagName
        className={`${className} animate-pulse bg-gray-200`}
        style={{ minHeight: '1em', minWidth: '100px' }}
        {...props}
      />
    );
  }

  if (error) {
    console.error('EditableContent error:', error);
    return (
      <TagName className={className} {...props}>
        {renderContent(defaultValue || children?.toString() || contentKey)}
      </TagName>
    );
  }

  if (user && isEditing) {
    return (
      <div className="relative group border border-primary-300 rounded-md p-1 bg-white">
        {multiline ? (
          <textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className={`w-full min-h-[100px] p-2 border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500 ${className}`}
          />
        ) : (
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className={`w-full p-2 border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500 ${className}`}
          />
        )}

        <div className="flex justify-end space-x-2 mt-2">
          <button
            type="button"
            onClick={handleCancel}
            className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded flex items-center"
            disabled={isSaving}
          >
            <SafeIcon icon={FiX} className="w-3 h-3 mr-1" />
            Ακύρωση
          </button>

          <button
            type="button"
            onClick={handleSave}
            className="px-2 py-1 text-xs bg-primary-600 hover:bg-primary-700 text-white rounded flex items-center"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-white mr-1" />
                Αποθήκευση...
              </>
            ) : (
              <>
                <SafeIcon icon={FiSave} className="w-3 h-3 mr-1" />
                Αποθήκευση
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative inline-block">
      <TagName className={className} {...props}>
        {renderContent(localContent)}
      </TagName>

      {user && (
        <button
          type="button"
          onClick={handleEdit}
          className="absolute -top-2 -right-2 bg-primary-100 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-primary-200"
          title="Επεξεργασία περιεχομένου"
        >
          <SafeIcon icon={FiEdit} className="w-3 h-3 text-primary-600" />
        </button>
      )}
    </div>
  );
};

export default EditableContent;
